import { reactive } from "vue";
import type { Ref } from "vue";
import {
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  type ShopListInput,
} from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetAuthType,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getChainList } from "@/common/graphql/chain";
import { getShopList } from "@/common/graphql/shop";
import { checkChainListDisplay } from "@/common/business";
import { getWordList } from "@/common/graphql/expression";
import { DELIVERY_CAP_SETTING_FILE_NAME } from "../constants";
import type { ChainItem, ShopItem, AreaListItem, ShopSelectValue, ChainSelectValue } from "../types";

/**
 * 画面初期ロード処理
 * onMounted で呼び出す初期化ロジック（文言/権限/チェーン/店舗/エリア取得）を管理する
 */
export const useDeliveryCapSettingInit = (params: {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  chainListItems: Ref<ChainItem[]>;
  chainSelectValues: Ref<ChainSelectValue>;
  shopListItems: Ref<ShopItem[]>;
  shopSelectValues: Ref<ShopSelectValue>;
  areaListItems: Ref<(AreaListItem | null)[]>;
  shopListInput: ShopListInput;
}) => {
  const {
    errdialog,
    errMessage,
    chainListItems,
    chainSelectValues,
    shopListItems,
    shopSelectValues,
    areaListItems,
    shopListInput,
  } = params;

  const wordList = reactive<Record<string, string>>({});

  // ユーザ権限取得
  const chainViewFlag = checkChainListDisplay(localStorageGetAuthType());

  /**
   * 初期ロード実行
   */
  const runInitialLoad = async (): Promise<void> => {
    try {
      const apolloClient = await generateApolloClient();
      Object.assign(
        wordList,
        await getWordList(
          localStorageGetChainCode(),
          DELIVERY_CAP_SETTING_FILE_NAME,
          localStorageGetCustomerCode()
        )
      );

      // 運用管理者以外の場合、チェーンコードはログインユーザのチェーンコードにする
      if (chainViewFlag) {
        // チェーン情報を取得する
        chainListItems.value = await getChainList(localStorageGetCustomerCode());
        chainSelectValues.value.chain_code = "";
        shopListInput.chain_code = "";
        shopListItems.value = [];
      } else {
        // チェーン情報を設定する
        chainSelectValues.value.chain_code = localStorageGetChainCode();
        shopListInput.chain_code = localStorageGetChainCode();

        //店舗情報を取得する
        shopListItems.value = await getShopList(shopListInput);

        // エリアデータ取得処理
        const listArea = await apolloClient.query<
          ListDeliveryAreaQuery,
          ListDeliveryAreaQueryVariables
        >({
          query: ListDeliveryAreaDocument,
          variables: {
            chain_code: chainSelectValues.value.chain_code,
            customer_code: localStorageGetCustomerCode(),
            shop_code: shopSelectValues.value.shop_code,
          },
          fetchPolicy: "no-cache",
        });
        areaListItems.value = listArea.data.listDeliveryArea || [];
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "予期しないエラーが発生しました。";
    }
  };

  return {
    wordList,
    chainViewFlag,
    runInitialLoad,
  };
};
